import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { JobSearchComboBox } from '@/components/shared/job-search-combo-box';
import type { JobOption } from '@/components/shared/job-search-combo-box';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import { FieldError } from '@/app/components/ui/field-error';
import {
  useCreateAndApproveRescuePlan,
  useCreateRescuePlan,
  useJSA,
  useRescuePlan,
  useRescuePlans,
  useUpdateRescuePlan,
} from '@/hooks/use-safety';
import { useOrganizationSettings, useOrgToday } from '@/hooks/use-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';
import {
  fieldErrorMessage,
  parseFormErrors,
  rowErrorMessage,
  summarizeFormErrors,
  type FormErrors,
} from '@/lib/form-errors';
import { isExistingPlanConflict, validateRescuePlanForm } from '../plan-form-validation';

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  project: 'Project',
  location_fk: 'Location',
  location: 'Specific Area / Details',
  date: 'Date',
  jsa: 'JSA',
  safe_work_plan: 'Safe Work Plan',
  rescue_team_description: 'Rescue Team Description',
  rescue_equipment_location: 'Rescue Equipment Location',
  communication_protocol: 'Communication Protocol',
  steps: 'Rescue step',
  equipment: 'Rescue equipment',
  nearest_hospital: 'Nearest Hospital',
  hospital_route_description: 'Hospital Route Description',
  hospital_phone: 'Hospital Phone',
  emergency_services_phone: 'Emergency Services Phone',
  estimated_rescue_time_minutes: 'Estimated Rescue Time',
  site_access_notes: 'Site Access Notes',
  additional_hazards: 'Additional Hazards',
};

interface StepRow {
  step_number: number;
  description: string;
  responsible_role: string;
  estimated_time_minutes: number | undefined;
  order: number;
}

interface EquipmentRow {
  equipment_name: string;
  quantity: number;
  location_staged: string;
  asset?: number | null;
  notes: string;
}

export function CreateRescuePlanPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const planId = id ? parseInt(id, 10) : 0;
  const isEditing = planId > 0;
  const linkedSwpId = searchParams.get('from_swp') ? parseInt(searchParams.get('from_swp')!, 10) : undefined;
  const linkedJsaId = searchParams.get('from_jsa') ? parseInt(searchParams.get('from_jsa')!, 10) : undefined;
  const createMutation = useCreateRescuePlan();
  const createAndApproveMutation = useCreateAndApproveRescuePlan();
  const updateMutation = useUpdateRescuePlan();
  const { data: existingPlan } = useRescuePlan(planId);
  const { data: linkedJsa } = useJSA(linkedJsaId ?? 0);
  const { hasMinModuleRole } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');
  const orgToday = useOrgToday();
  const {
    data: organizationSettings,
    isPending: organizationSettingsPending,
    fetchStatus: organizationSettingsFetchStatus,
  } = useOrganizationSettings();
  const organizationSettingsUnresolved =
    organizationSettingsPending && organizationSettingsFetchStatus !== 'paused';
  const emergencyServicesPhoneEdited = useRef(false);
  const initializedFromPlan = useRef(false);

  const [title, setTitle] = useState('');
  const [selectedProject, setSelectedProject] = useState<JobOption | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState('');
  const [date, setDate] = useState(orgToday);
  const [rescueTeamDescription, setRescueTeamDescription] = useState('');
  const [rescueEquipmentLocation, setRescueEquipmentLocation] = useState('');
  const [communicationProtocol, setCommunicationProtocol] = useState('');
  const [nearestHospital, setNearestHospital] = useState('');
  const [hospitalRouteDescription, setHospitalRouteDescription] = useState('');
  const [hospitalPhone, setHospitalPhone] = useState('');
  const [emergencyServicesPhone, setEmergencyServicesPhone] = useState('');
  const [estimatedRescueTime, setEstimatedRescueTime] = useState('');
  const [siteAccessNotes, setSiteAccessNotes] = useState('');
  const [additionalHazards, setAdditionalHazards] = useState('');
  const [selectedJsaId, setSelectedJsaId] = useState<number | null>(
    linkedJsaId ?? null
  );
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [equipment, setEquipment] = useState<EquipmentRow[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // "This job already has a rescue plan" — look the current one up so the
  // message can link straight to it.
  const projectError = fieldErrorMessage(formErrors, 'project');
  const projectHasPlan = isExistingPlanConflict(formErrors?.fields.project);
  const { data: plansForProject } = useRescuePlans(
    { project: selectedProject?.id, page_size: 10 },
    projectHasPlan && Boolean(selectedProject),
  );
  const conflictingPlan = plansForProject?.results
    .filter((plan) => plan.project === selectedProject?.id)
    .sort((a, b) => Number(b.is_current) - Number(a.is_current))[0];

  const showFormErrors = (errors: FormErrors) => {
    setFormErrors(errors);
    window.setTimeout(() => {
      const summary = errorSummaryRef.current;
      if (summary && typeof summary.scrollIntoView === 'function') {
        summary.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  };

  useEffect(() => {
    if (emergencyServicesPhoneEdited.current) return;

    if (organizationSettings) {
      setEmergencyServicesPhone(organizationSettings.emergency_services_number);
    }
  }, [organizationSettings]);

  useEffect(() => {
    if (!existingPlan || initializedFromPlan.current) return;
    initializedFromPlan.current = true;
    setTitle(existingPlan.title);
    setSelectedProject(
      existingPlan.project && existingPlan.project_display
        ? {
            id: existingPlan.project,
            name: existingPlan.project_display.name,
            job_number: existingPlan.project_display.project_number,
            client_name: '',
            location_fk: existingPlan.location_fk,
            location_fk_display: existingPlan.location_fk_display,
          }
        : null
    );
    setSelectedLocation(existingPlan.location_fk_display);
    setLocation(existingPlan.location);
    setDate(existingPlan.date);
    setRescueTeamDescription(existingPlan.rescue_team_description);
    setRescueEquipmentLocation(existingPlan.rescue_equipment_location);
    setCommunicationProtocol(existingPlan.communication_protocol);
    setNearestHospital(existingPlan.nearest_hospital);
    setHospitalRouteDescription(existingPlan.hospital_route_description);
    setHospitalPhone(existingPlan.hospital_phone);
    emergencyServicesPhoneEdited.current = true;
    setEmergencyServicesPhone(existingPlan.emergency_services_phone);
    setEstimatedRescueTime(
      existingPlan.estimated_rescue_time_minutes?.toString() ?? ''
    );
    setSiteAccessNotes(existingPlan.site_access_notes);
    setAdditionalHazards(existingPlan.additional_hazards);
    setSelectedJsaId(existingPlan.jsa);
    setSteps(
      existingPlan.steps.map((step, index) => ({
        step_number: index + 1,
        description: step.description,
        responsible_role: step.responsible_role,
        estimated_time_minutes: step.estimated_time_minutes ?? undefined,
        order: index,
      }))
    );
    setEquipment(
      existingPlan.equipment.map((item) => ({
        equipment_name: item.equipment_name,
        quantity: item.quantity,
        location_staged: item.location_staged,
        asset: item.asset,
        notes: item.notes,
      }))
    );
  }, [existingPlan]);

  useEffect(() => {
    if (isEditing || !linkedJsa?.project || !linkedJsa.project_display) return;
    setSelectedProject({
      id: linkedJsa.project,
      name: linkedJsa.project_display.name,
      job_number: linkedJsa.project_display.project_number,
      client_name: '',
      location_fk: null,
    });
    setSelectedJsaId(null);
  }, [isEditing, linkedJsa]);

  const addStep = () => {
    setSteps([
      ...steps,
      {
        step_number: steps.length + 1,
        description: '',
        responsible_role: '',
        estimated_time_minutes: undefined,
        order: steps.length,
      },
    ]);
  };

  const removeStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const updateStep = (index: number, field: keyof StepRow, value: string | number | undefined) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], [field]: value };
    setSteps(updated);
  };

  const addEquipment = () => {
    setEquipment([
      ...equipment,
      { equipment_name: '', quantity: 1, location_staged: '', notes: '' },
    ]);
  };

  const removeEquipment = (index: number) => {
    setEquipment(equipment.filter((_, i) => i !== index));
  };

  const updateEquipment = (index: number, field: keyof EquipmentRow, value: string | number) => {
    const updated = [...equipment];
    updated[index] = { ...updated[index], [field]: value };
    setEquipment(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (organizationSettingsUnresolved) return;
    const submitter = (e.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement | null;
    const shouldCreateAndApprove = submitter?.value === 'create-and-approve';
    setFormErrors(null);

    // Catch what the backend would reject before sending anything, so the
    // problems show up next to the fields instead of a silent 400.
    const validationErrors = validateRescuePlanForm(
      { steps, rescueTeamDescription, communicationProtocol, emergencyServicesPhone },
      { approve: shouldCreateAndApprove && !isEditing },
    );
    if (validationErrors) {
      showFormErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        title,
        jsa: selectedJsaId,
        safe_work_plan: existingPlan?.safe_work_plan ?? linkedSwpId,
        project: selectedProject?.id ?? null,
        project_name: selectedProject?.name || '',
        project_number: selectedProject?.job_number || '',
        location_fk: selectedLocation?.id || null,
        location,
        date,
        rescue_team_description: rescueTeamDescription,
        rescue_equipment_location: rescueEquipmentLocation,
        communication_protocol: communicationProtocol,
        nearest_hospital: nearestHospital,
        hospital_route_description: hospitalRouteDescription,
        hospital_phone: hospitalPhone,
        emergency_services_phone: emergencyServicesPhone,
        estimated_rescue_time_minutes: estimatedRescueTime
          ? parseInt(estimatedRescueTime, 10)
          : isEditing
            ? null
            : undefined,
        site_access_notes: siteAccessNotes,
        additional_hazards: additionalHazards,
        steps: steps.map((s, i) => ({
          step_number: i + 1,
          description: s.description,
          responsible_role: s.responsible_role,
          estimated_time_minutes: s.estimated_time_minutes,
          order: i,
        })),
        equipment: equipment.map((eq) => ({
          equipment_name: eq.equipment_name,
          quantity: eq.quantity,
          location_staged: eq.location_staged,
          asset: eq.asset ?? undefined,
          notes: eq.notes,
        })),
      };
      const plan = isEditing
        ? await updateMutation.mutateAsync({ id: planId, data: payload })
        : shouldCreateAndApprove
          ? await createAndApproveMutation.mutateAsync(payload)
          : await createMutation.mutateAsync(payload);

      navigate(`/safety/rescue-plans/${plan.id}`);
    } catch (error) {
      showFormErrors(
        parseFormErrors(error, 'The rescue plan could not be saved. Please try again.'),
      );
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/rescue-plans')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Rescue Plan' : 'New Rescue Plan'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Update this draft rescue procedure' : 'Create a site-specific rescue procedure'}
          </p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Rescue Plan - East Face"
                required
                aria-invalid={Boolean(fieldErrorMessage(formErrors, 'title'))}
              />
              <FieldError message={fieldErrorMessage(formErrors, 'title')} />
            </div>
            <div>
              <Label>Project</Label>
              <JobSearchComboBox
                value={selectedProject}
                onChange={(job) => {
                  setSelectedProject(job);
                  if (job?.location_fk_display && !selectedLocation) {
                    setSelectedLocation({
                      id: job.location_fk_display.id,
                      name: job.location_fk_display.name,
                      type: job.location_fk_display.type,
                    });
                  }
                }}
              />
              {projectError && (
                <FieldError message={projectError}>
                  {conflictingPlan && (
                    <>
                      {' '}
                      <Link
                        to={`/safety/rescue-plans/${conflictingPlan.id}`}
                        className="font-medium underline"
                      >
                        Open the existing rescue plan
                      </Link>
                    </>
                  )}
                </FieldError>
              )}
            </div>
            <div>
              <Label>Location</Label>
              <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Specific Area / Details</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. East face, 3rd floor scaffolding"
                />
              </div>
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
                <FieldError message={fieldErrorMessage(formErrors, 'date')} />
              </div>
            </div>
            {isEditing && existingPlan?.jsa && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedJsaId === existingPlan.jsa}
                  onChange={(event) =>
                    setSelectedJsaId(
                      event.target.checked ? existingPlan.jsa : null
                    )
                  }
                />
                Keep linked to{' '}
                {existingPlan.jsa_title || `JSA #${existingPlan.jsa}`}
              </label>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rescue Team &amp; Communication</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="team">Rescue Team Description</Label>
              <Textarea
                id="team"
                value={rescueTeamDescription}
                onChange={(e) => setRescueTeamDescription(e.target.value)}
                rows={3}
                placeholder="Who is on the rescue team, their roles..."
                aria-invalid={Boolean(fieldErrorMessage(formErrors, 'rescue_team_description'))}
              />
              <FieldError message={fieldErrorMessage(formErrors, 'rescue_team_description')} />
            </div>
            <div>
              <Label htmlFor="equipLocation">Rescue Equipment Location</Label>
              <Textarea
                id="equipLocation"
                value={rescueEquipmentLocation}
                onChange={(e) => setRescueEquipmentLocation(e.target.value)}
                rows={2}
                placeholder="Where rescue gear is staged..."
              />
            </div>
            <div>
              <Label htmlFor="comms">Communication Protocol</Label>
              <Textarea
                id="comms"
                value={communicationProtocol}
                onChange={(e) => setCommunicationProtocol(e.target.value)}
                rows={2}
                placeholder="How rescue is initiated - radio channel, whistle signals..."
                aria-invalid={Boolean(fieldErrorMessage(formErrors, 'communication_protocol'))}
              />
              <FieldError message={fieldErrorMessage(formErrors, 'communication_protocol')} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Rescue Steps</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addStep}>
              <Plus className="h-4 w-4 mr-1" />
              Add Step
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <FieldError message={fieldErrorMessage(formErrors, 'steps')} />
            {steps.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No rescue steps added yet. Click "Add Step" to begin.
              </p>
            ) : (
              steps.map((step, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Step #{index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeStep(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={step.description}
                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                      rows={2}
                      placeholder="e.g., Rescuer ascends to casualty using secondary line"
                      aria-invalid={Boolean(rowErrorMessage(formErrors, 'steps', index, 'description'))}
                    />
                    <FieldError message={rowErrorMessage(formErrors, 'steps', index, 'description')} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Responsible Role</Label>
                      <Input
                        value={step.responsible_role}
                        onChange={(e) => updateStep(index, 'responsible_role', e.target.value)}
                        placeholder="e.g., Rescuer 1"
                      />
                    </div>
                    <div>
                      <Label>Estimated Time (min)</Label>
                      <Input
                        type="number"
                        value={step.estimated_time_minutes ?? ''}
                        onChange={(e) =>
                          updateStep(
                            index,
                            'estimated_time_minutes',
                            e.target.value ? parseInt(e.target.value, 10) : undefined
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Rescue Equipment</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addEquipment}>
              <Plus className="h-4 w-4 mr-1" />
              Add Equipment
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {equipment.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No rescue equipment added yet.
              </p>
            ) : (
              equipment.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Equipment #{index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEquipment(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Equipment Name</Label>
                      <Input
                        value={item.equipment_name}
                        onChange={(e) =>
                          updateEquipment(index, 'equipment_name', e.target.value)
                        }
                        placeholder="e.g., Rescue descent device"
                      />
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateEquipment(index, 'quantity', parseInt(e.target.value, 10) || 1)
                        }
                      />
                    </div>
                    <div>
                      <Label>Staging Location</Label>
                      <Input
                        value={item.location_staged}
                        onChange={(e) =>
                          updateEquipment(index, 'location_staged', e.target.value)
                        }
                        placeholder="e.g., Roof anchor point A"
                      />
                    </div>
                  </div>
                  <FieldError message={rowErrorMessage(formErrors, 'equipment', index)} />
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Emergency Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hospital">Nearest Hospital</Label>
                <Input
                  id="hospital"
                  value={nearestHospital}
                  onChange={(e) => setNearestHospital(e.target.value)}
                  placeholder="Name and address"
                />
              </div>
              <div>
                <Label htmlFor="hospitalPhone">Hospital Phone</Label>
                <Input
                  id="hospitalPhone"
                  value={hospitalPhone}
                  onChange={(e) => setHospitalPhone(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="route">Hospital Route Description</Label>
              <Textarea
                id="route"
                value={hospitalRouteDescription}
                onChange={(e) => setHospitalRouteDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="emergency">Emergency Services Phone *</Label>
                <Input
                  id="emergency"
                  value={emergencyServicesPhone}
                  onChange={(e) => {
                    emergencyServicesPhoneEdited.current = true;
                    setEmergencyServicesPhone(e.target.value);
                  }}
                  placeholder="Enter the local emergency number"
                  required
                />
                <FieldError message={fieldErrorMessage(formErrors, 'emergency_services_phone')} />
                {!emergencyServicesPhone && (
                  <p className="mt-1 text-sm text-amber-700">
                    Confirm the emergency number for this location.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="rescueTime">Estimated Rescue Time (min)</Label>
                <Input
                  id="rescueTime"
                  type="number"
                  value={estimatedRescueTime}
                  onChange={(e) => setEstimatedRescueTime(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="access">Site Access Notes</Label>
              <Textarea
                id="access"
                value={siteAccessNotes}
                onChange={(e) => setSiteAccessNotes(e.target.value)}
                rows={2}
                placeholder="How emergency vehicles access the site..."
              />
            </div>
            <div>
              <Label htmlFor="hazards">Additional Hazards</Label>
              <Textarea
                id="hazards"
                value={additionalHazards}
                onChange={(e) => setAdditionalHazards(e.target.value)}
                rows={2}
                placeholder="Site-specific hazards that affect rescue..."
              />
            </div>
          </CardContent>
        </Card>

        {formErrors && (
          <div ref={errorSummaryRef}>
            <Alert variant="destructive">
              <AlertTitle>
                {isEditing
                  ? 'The rescue plan could not be saved'
                  : 'The rescue plan could not be created'}
              </AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-5 space-y-1">
                  {summarizeFormErrors(formErrors, FIELD_LABELS).map((line, index) => (
                    <li key={`${index}-${line}`}>{line}</li>
                  ))}
                </ul>
                {conflictingPlan && (
                  <Link
                    to={`/safety/rescue-plans/${conflictingPlan.id}`}
                    className="font-medium underline"
                  >
                    Open the existing rescue plan
                  </Link>
                )}
              </AlertDescription>
            </Alert>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/safety/rescue-plans')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            value="create"
            disabled={
              organizationSettingsUnresolved ||
              createMutation.isPending ||
              createAndApproveMutation.isPending ||
              updateMutation.isPending
            }
          >
            {isEditing
              ? updateMutation.isPending
                ? 'Saving...'
                : 'Save Changes'
              : createMutation.isPending
                ? 'Creating...'
                : 'Create Rescue Plan'}
          </Button>
          {!isEditing && canManageSafety && (
            <Button
              type="submit"
              value="create-and-approve"
              disabled={
                organizationSettingsUnresolved ||
                createMutation.isPending ||
                createAndApproveMutation.isPending
              }
            >
              {createAndApproveMutation.isPending ? 'Approving...' : 'Create & approve'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
